import type { Media, Persisted } from '@tunasong/schemas'
import { useCallback, useEffect, useState } from 'react'
import { getDurationLabel } from '../duration/index.js'
import type { PlayerProps } from './audio-player.js'
import { useAudioPlayer } from './audio-player.js'

interface PlayerControl extends PlayerProps {
  el: HTMLAudioElement | null
  name?: string
  active?: Pick<Persisted<Media>, 'id' | 'name' | 'storageUrls'>
  media: Pick<Persisted<Media>, 'id' | 'name' | 'storageUrls'>[]
  /**
   * When false, a track's `ended` event does NOT advance to the next track —
   * playback stops on the current entry and waits for explicit user input.
   * Defaults to true (pre-existing continuous-playlist behavior). Used by the
   * Playlist Performance Mode, where autoplaying the next song mid-gig is
   * never acceptable.
   */
  autoAdvance?: boolean
  onSelect(idx: number): void
}

export const usePlayerControl = (props: PlayerControl) => {
  const { active, onSelect, el, media, autoAdvance = true, ...restProps } = props

  const audioPlayer = useAudioPlayer({
    el: el ?? null,
    ...restProps,
  })

  const activeIdx = active ? media.findIndex(m => m.id === active.id) : -1
  const hasPrev = activeIdx > 0
  const hasNext = activeIdx >= 0 && activeIdx < media.length - 1

  const handlePrev = useCallback(() => {
    if (!hasPrev) {
      return
    }
    onSelect(activeIdx - 1)
  }, [activeIdx, hasPrev, onSelect])

  const handleNext = useCallback(() => (hasNext ? onSelect(activeIdx + 1) : undefined), [activeIdx, hasNext, onSelect])

  /** The Audio element may not be ready to play when we invoke play(). Hence, we set autoPlay to start when it's ready */
  const [autoPlay, setAutoPlay] = useState(false)

  const handleEnded = useCallback(() => {
    if (!autoAdvance) {
      // Performance-mode semantics: stop on the current entry and clear the
      // pending-autoplay latch so a later `canplay` doesn't restart audio.
      setAutoPlay(false)
      return
    }
    if (hasNext) {
      setAutoPlay(true)
      handleNext()
    }
  }, [autoAdvance, handleNext, hasNext])

  // Trigger a start when we're already playing
  const handleStart = useCallback(() => {
    if (autoPlay) {
      void audioPlayer.play()
    }
  }, [autoPlay, audioPlayer])

  const play = useCallback(async () => {
    setAutoPlay(true)
    return audioPlayer.play()
  }, [audioPlayer])

  const pause = useCallback(() => {
    setAutoPlay(false)
    audioPlayer.pause()
  }, [audioPlayer])

  const togglePlay = useCallback(() => {
    // If we're paused, toggle will play.
    if (!audioPlayer.playing) {
      setAutoPlay(true)
    }
    void audioPlayer.togglePlay()
  }, [audioPlayer])

  const [seeking, setSeeking] = useState(false)
  /** Events from the player */
  useEffect(() => {
    if (!el) {
      return
    }
    const handleSetSeeking = () => setSeeking(true)
    const handleClearSeeking = () => setSeeking(false)

    el.addEventListener('seeking', handleSetSeeking)
    el.addEventListener('seeked', handleClearSeeking)
    el.addEventListener('ended', handleEnded)
    el.addEventListener('canplay', handleStart)

    return () => {
      el.removeEventListener('seeking', handleSetSeeking)
      el.removeEventListener('seeked', handleClearSeeking)
      el.removeEventListener('ended', handleEnded)
      el.removeEventListener('canplay', handleStart)
      // Intentionally no setSeeking(false) here. useAudioPlayer returns a new
      // object each render, so handleStart's identity churns, this effect
      // re-runs on every render, and the cleanup runs every render. A
      // setState in the cleanup pushed an enqueueConcurrentHookUpdate per
      // render — after ~50 renders, React's nestedUpdateCount tripped and
      // raised "Maximum update depth exceeded" (TUNASONG-VP / GH #1338).
      // The listeners are already removed above, so no further 'seeking' /
      // 'seeked' events can fire on this element; there's nothing to reset.
    }
  }, [el, handleEnded, handleStart])

  return {
    ...audioPlayer,
    play,
    pause,
    togglePlay,
    progressLabel: `${getDurationLabel(el?.currentTime ?? 0)}/${getDurationLabel(audioPlayer.duration)}`,
    prev: hasPrev ? handlePrev : undefined,
    next: hasNext ? handleNext : undefined,
    seeking,
  }
}
